<template>
  <div>
    <!-- État de chargement -->
    <div 
      v-if="conversationIsLoading" 
      class="bg-gradient-to-r from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 px-6 py-4 border-t dark:border-gray-700">
      <div class="flex items-center justify-center gap-3">
        <div class="w-5 h-5 border-3 border-teal-300 border-t-teal-600 rounded-full animate-spin"></div>
        <span class="text-sm font-medium text-teal-800 dark:text-teal-300">Chargement de la conversation...</span>
      </div>
    </div>

    <!-- Conversation non assignée ou assignée à un autre agent -->
    <div 
      v-else-if="user?.id !== conversation?.agent_id"
      class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 px-6 py-5 border-t dark:border-gray-700">
      <div class="flex flex-col items-center text-center gap-4 max-w-md mx-auto">
        <!-- Icône -->
        <div class="w-14 h-14 bg-blue-500 dark:bg-blue-600 rounded-full flex items-center justify-center">
          <i class="las la-user-lock text-2xl text-white"></i>
        </div>
        
        <!-- Message -->
        <div class="space-y-2">
          <p class="text-base font-semibold text-blue-900 dark:text-blue-200">
            <span v-if="!conversation?.agent_id">
              Cette conversation n'est pas encore assignée
            </span>
            <span v-else>
              Conversation assignée à {{ conversation?.agent?.firstname }} {{ conversation?.agent?.lastname }}
            </span>
          </p>
          <p class="text-sm text-blue-700 dark:text-blue-300">
            Souhaitez-vous prendre cette conversation en charge ?
          </p>
        </div>

        <!-- Bouton d'action -->
        <button
          @click="assignedConversation"
          class="group px-6 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 flex items-center gap-2">
          <i class="las la-hand-paper text-lg group-hover:scale-110 transition-transform"></i>
          <span>Prendre en charge</span>
        </button>
      </div>
    </div>

    <!-- Zone de réponse active -->
    <template v-else-if="messageIsSuccess && conversationIsSuccess">
      
      <!-- Prévisualisation du message cité -->
      <transition name="slide-down">
        <div 
          v-if="parentReply" 
          class="bg-teal-50 dark:bg-teal-900/20 border-l-4 border-teal-500 mx-4 my-2 rounded-r-lg overflow-hidden">
          <div class="flex items-start gap-3 p-3">
            <!-- Barre colorée (déjà en border-left) -->
            
            <!-- Contenu du message cité -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <i class="las la-reply text-teal-600 dark:text-teal-400 text-sm"></i>
                <span class="text-xs font-semibold text-teal-800 dark:text-teal-300">Réponse à</span>
              </div>
              
              <!-- Message avec icône média -->
              <div class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <template v-if="parentReply?.type !== 'chat'">
                  <i :class="getMediaIcon(parentReply?.type)" class="text-gray-500 dark:text-gray-400"></i>
                  <span class="truncate">
                    {{ parentReply?.type === 'ptt' ? 'Message vocal' : toPascalCase(parentReply?.type) }}
                    {{ parentReply?.filename ? ` - ${parentReply.filename}` : '' }}
                  </span>
                </template>
                <span v-else class="truncate" :class="!parentReply?.selfMessage && !parentReply?.isRead ? 'font-semibold' : ''">
                  {{ JSON.parse(parentReply?.content) ?? "" }}
                </span>
              </div>
            </div>

            <!-- Bouton fermer -->
            <button
              @click="clearParentReply"
              type="button"
              class="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
              <i class="las la-times text-lg"></i>
            </button>
          </div>
        </div>
      </transition>

      <!-- Barre d'outils et input -->
      <form 
        ref="form" 
        @submit.prevent="sendMessage(conversation?.chat_id ?? null)"
        class="bg-white dark:bg-slate-800 border-t dark:border-gray-700 px-4 py-3">
        
        <div class="flex items-end gap-2">
          
          <!-- Bouton pièce jointe / supprimer vocal -->
          <button
            v-if="!messageIsLoading"
            type="button"
            @click="isVocal ? stopRecording('delete') : null"
            :class="[
              'flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-all',
              isVocal 
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50' 
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            ]">
            <label v-if="!isVocal" for="file_input" class="cursor-pointer w-full h-full flex items-center justify-center">
              <i class="las la-paperclip text-2xl"></i>
              <input type="file" id="file_input" @change="setFileReply" class="hidden">
            </label>
            <i v-else class="las la-trash-alt text-xl"></i>
          </button>

          <!-- Input principal ou enregistrement vocal -->
          <div class="flex-1 min-w-0">
            
            <!-- Mode texte normal -->
            <div 
              v-if="!isVocal && !messageIsLoading"
              class="bg-gray-100 dark:bg-slate-700 rounded-2xl px-4 py-2 transition-all focus-within:ring-2 focus-within:ring-teal-500">
              <textarea
                v-model="messageReply"
                @keypress="pressEnter"
                :required="!fileReply"
                rows="1"
                placeholder="Écrivez votre message..."
                class="w-full bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none outline-none max-h-32"
                style="min-height: 24px;"
              ></textarea>
            </div>

            <!-- Mode enregistrement vocal -->
            <div 
              v-else-if="isVocal"
              class="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-2xl px-4 py-3 border border-red-200 dark:border-red-800">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <!-- Icône pulsante -->
                  <div class="relative">
                    <div class="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div class="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  </div>
                  <span class="text-sm font-semibold text-red-800 dark:text-red-300">
                    Enregistrement en cours...
                  </span>
                </div>
                
                <!-- Timer -->
                <span class="text-lg font-bold text-red-700 dark:text-red-400 tabular-nums">
                  {{ vocalTimeOut }}
                </span>
              </div>
            </div>

            <!-- Mode chargement -->
            <div 
              v-else-if="messageIsLoading"
              class="bg-gray-100 dark:bg-slate-700 rounded-2xl px-4 py-3">
              <div class="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <div class="w-4 h-4 border-2 border-gray-300 dark:border-gray-500 border-t-teal-500 rounded-full animate-spin"></div>
                <span class="text-sm">Envoi en cours...</span>
              </div>
            </div>
          </div>

          <!-- Bouton microphone / envoyer -->
          <button
            v-if="!messageIsLoading"
            type="button"
            @click="isVocal ? stopRecording('submit') : (messageReply || fileReply ? sendMessage(conversation?.chat_id) : startRecording())"
            :class="[
              'flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full transition-all shadow-md hover:shadow-lg active:scale-90',
              messageReply || fileReply || isVocal
                ? 'bg-gradient-to-br from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
            ]">
            <i 
              :class="messageReply || fileReply || isVocal ? 'las la-paper-plane' : 'las la-microphone'" 
              class="text-2xl"
            ></i>
          </button>
        </div>

        <!-- Prévisualisation du fichier attaché -->
        <transition name="slide-down">
          <div 
            v-if="fileReply && !isVocal"
            class="mt-3 flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <i class="las la-file text-2xl text-blue-600 dark:text-blue-400"></i>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {{ fileReply.name }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ (fileReply.size / 1024).toFixed(2) }} KB
              </p>
            </div>
            <button
              @click="fileReply = null"
              type="button"
              class="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
              <i class="las la-times text-lg"></i>
            </button>
          </div>
        </transition>
      </form>
    </template>

    <!-- État d'erreur -->
    <div 
      v-else 
      class="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 px-6 py-4 border-t dark:border-gray-700">
      <div class="flex items-center justify-center gap-3 text-center">
        <i class="las la-exclamation-triangle text-2xl text-red-600 dark:text-red-400"></i>
        <p class="text-sm font-semibold text-red-800 dark:text-red-300">
          Impossible de répondre à cette conversation. 
          <button @click="$emit('retry')" class="underline hover:no-underline ml-1">
            Réessayer
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { toPascalCase } from '@/utils';
import { mapActions } from 'vuex';
import { mapFields } from 'vuex-map-fields';

export default {
  name: "MessageReply",
  emits: ['retry'],
  data() {
    return {
      chunks: [],
      isVocal: false,
      vocalIsPaused: false,
      vocalIsPausedAnPlay: false,
      vocalTimeOutId: null,
      vocalTimeOut: "00:00",
      vocalTimeSecondes: 0,
      mediaRecorder: null,
      chunksRecorder: [],
      streamRecorder: "",
      previewRecorder: null,
    }
  },
  computed: {
    ...mapFields("messages/list", {
      messageIsLoading: "isLoading",
      messageIsSuccess: "isSuccess",
    }),
    ...mapFields("auth/user", {
      user: "item",
    }),
    ...mapFields("messages/reply", {
      fileReply: "fileReply",
      messageReply: "messageReply",
      parentReply: "parentReply",
      pttReply: "pttReply",
      isLoadingReply: "isLoading"
    }),
    ...mapFields("conversations/show", {
      conversationIsLoading: "isLoading",
      conversationIsSuccess: "isSuccess",
      conversation: "item",
    }),
  },
  methods: {
    toPascalCase,
    ...mapActions({
      sendMessage: "messages/reply/sendMessage",
      assignedConversation: "conversations/update/assignedConversation"
    }),

    getMediaIcon(type) {
      const icons = {
        audio: 'las la-music',
        image: 'las la-image',
        video: 'las la-video',
        ptt: 'las la-microphone',
        location: 'las la-map-marker',
        vcard: 'las la-address-card',
        document: 'las la-file-alt'
      };
      return icons[type] || 'las la-file';
    },

    setFileReply(files) {
      this.fileReply = files.target.files[0]
    },

    clearParentReply() {
      this.parentReply = null
    },

    pressEnter(event) {
      if (event.keyCode === 13 && !event.shiftKey) {
        event.preventDefault();
        if (!this.isLoadingReply) {
          this.sendMessage(this.conversation?.chat_id);
        }
      }
    },

    // Vocal
    startVocalTimeout() {
      if (this.vocalTimeOutId != null) this.clearVocalTimeOut()
      this.vocalTimeOutId = setInterval(() => {
        this.vocalTimeSecondes += 1
        this.parseVocalTimeOut()
      }, 1000)
    },

    parseVocalTimeOut() {
      if (this.vocalTimeSecondes >= 3600) {
        this.vocalTimeOut = '01:00:00'
        this.pauseRecording()
      } else {
        let mins = ('0' + parseInt(this.vocalTimeSecondes / 60)).slice(-2)
        let secondes = ('0' + parseInt(this.vocalTimeSecondes % 60)).slice(-2)
        this.vocalTimeOut = mins + ':' + secondes
      }
    },

    clearVocalTimeOut() {
      clearInterval(this.vocalTimeOutId);
      this.vocalTimeOutId = null
    },

    async startRecording() {
      this.isVocal = true;
      this.startVocalTimeout();

      try {
        this.streamRecorder = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaRecorder = new MediaRecorder(this.streamRecorder);
        this.mediaRecorder.start();
      } catch (err) {
        console.error("Erreur lors de l'accès au microphone : ", err);
        this.isVocal = false;
        this.clearVocalTimeOut();
      }
    },

    saveChunckRecording(event) {
      if (event.data.size > 0) {
        this.chunksRecorder.push(event.data);
      }
    },

    pauseRecording() {
      this.clearVocalTimeOut();
      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.streamRecorder.getTracks().forEach(str => {
          str.stop()
        });

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.chunksRecorder, { type: 'audio/webm;codecs=opus' });
          const audioURL = URL.createObjectURL(audioBlob);
          this.previewRecorder = audioURL;
          this.vocalIsPaused = true;
        }
      }
    },

    handleSubmitRecording(ispaused = false) {
      this.mediaRecorder.stop()
      const sendMsgAudio = () => {
        const audioBlob = new Blob(this.chunksRecorder, { type: 'audio/webm' });
        this.blobToBase64(audioBlob).then((dat) => {
          this.pttReply = dat
          this.sendMessage(this.conversation?.chat_id)

          this.vocalIsPaused = false,
          this.vocalIsPausedAnPlay = false;
          this.vocalTimeOutId = null;
          this.vocalTimeOut = "00:00";
          this.vocalTimeSecondes = 0;
          this.mediaRecorder = null;
          this.chunksRecorder = [];
          this.streamRecorder = "";
          this.previewRecorder = null;
        }).catch((err) => {
          console.error('Erreur conversion audio:', err);
        })
      }
      
      if (ispaused) { 
        sendMsgAudio(); 
        this.isVocal = false; 
        this.vocalIsPaused = false;
        this.vocalIsPausedAnPlay = false;
      } else {
        this.mediaRecorder.onstop = sendMsgAudio
      }
      this.chunksRecorder = []
    },

    stopRecording(action = 'delete') {
      this.clearVocalTimeOut()
      this.vocalTimeSecondes = 0

      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.streamRecorder.getTracks().forEach(str => {
          str.stop()
        });

        this.isVocal = false;
        this.previewRecorder = null;
        
        if (action === 'delete') {
          this.chunksRecorder = [];
        } else {
          const data = this.vocalIsPaused;
          this.handleSubmitRecording(data)
        }
      }
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
          this.chunksRecorder = []
        } catch (err) {
          reject(err)
        }
      });
    },
  },

  watch: {
    mediaRecorder() {
      if (this.mediaRecorder != null) {
        this.mediaRecorder.ondataavailable = this.saveChunckRecording
      }
    },
    conversation: {
      handler(newVal) {
        console.log('🔍 DEBUG Reply.vue - conversation changée:')
        console.log('  newVal.agent_id:', newVal?.agent_id)
        console.log('  newVal.agent:', newVal?.agent)
        console.log('  user.id:', this.user?.id)
        console.log('  user.id !== conversation.agent_id:', this.user?.id !== newVal?.agent_id)
      },
      deep: true,
      immediate: true
    }
  },

  beforeUnmount() {
    if (this.mediaRecorder) {
      this.stopRecording('delete');
    }
  }
}
</script>

<style scoped>
/* Animations */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Animation pulsation pour le point rouge d'enregistrement */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

/* Auto-resize du textarea */
textarea {
  field-sizing: content;
}
</style>